import React, { useState, useEffect } from 'react';
import { Gift, Ticket, ChevronRight, X, Sparkles, Loader2, Check, Star, Trophy } from 'lucide-react';
import { usePrizes, Prize } from '@/contexts/PrizeContext';
import { formatCurrency } from '@/lib/utils';
import { apiClient } from '@/lib/api-client';

interface PrizeOfferPopupProps {
  onSkip: () => void;
  onEnter: () => void;
  scBalance: number;
}

export function PrizeOfferPopup({ onSkip, onEnter, scBalance }: PrizeOfferPopupProps) {
  const { prizes, entryTiers, totalMyEntries, purchaseEntries } = usePrizes();
  const [selectedTier, setSelectedTier] = useState<number | null>(null);
  const [customAmount, setCustomAmount] = useState('');
  const [useCustom, setUseCustom] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [selectedPrize, setSelectedPrize] = useState<Prize | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [entriesAwarded, setEntriesAwarded] = useState(0);
  const [remainingBalance, setRemainingBalance] = useState(scBalance);

  const activePrizes = prizes.filter((p) => p.status === 'active');

  // Smart default tier selection based on balance
  useEffect(() => {
    if (selectedPrize && entryTiers.length > 0) {
      const sortedTiers = [...entryTiers].sort((a, b) => a.sc_amount - b.sc_amount);
      
      // Determine the ideal default amount based on balance thresholds
      let targetAmount = 0.1; // Default
      if (scBalance > 500) {
        targetAmount = 100;
      } else if (scBalance > 200) {
        targetAmount = 50;
      } else if (scBalance > 150) {
        targetAmount = 10;
      } else if (scBalance > 105) {
        targetAmount = 5;
      } else if (scBalance > 101) {
        targetAmount = 1;
      } else if (scBalance > 100.1) {
        targetAmount = 0.1;
      }
      
      // Find the tier closest to the target amount that the user can afford
      const affordableTiers = sortedTiers.filter(t => t.sc_amount <= scBalance);
      if (affordableTiers.length > 0) {
        // Find the tier with amount closest to or equal to targetAmount
        const matchingTier = affordableTiers.find(t => t.sc_amount === targetAmount) 
          || affordableTiers.reduce((closest, tier) => {
            if (tier.sc_amount <= targetAmount && tier.sc_amount > (closest?.sc_amount || 0)) {
              return tier;
            }
            return closest;
          }, affordableTiers[0]);
        
        if (matchingTier) {
          setSelectedTier(matchingTier.id);
        }
      }
    }
  }, [selectedPrize, entryTiers, scBalance]);

  if (activePrizes.length === 0) {
    return null;
  }

  const calculateEntries = (scAmount: number): number => {
    const sortedTiers = [...entryTiers].sort((a, b) => b.sc_amount - a.sc_amount);
    for (const tier of sortedTiers) {
      if (scAmount >= tier.sc_amount) {
        const multiplier = Math.floor(scAmount / tier.sc_amount);
        return multiplier * tier.entries_awarded;
      }
    }
    const baseTier = sortedTiers[sortedTiers.length - 1];
    if (baseTier) {
      return Math.floor(scAmount * (baseTier.entries_awarded / baseTier.sc_amount));
    }
    return Math.floor(scAmount * 10);
  };

  const getSelectedAmount = (): number => {
    if (useCustom) {
      return parseFloat(customAmount) || 0;
    }
    if (selectedTier !== null) {
      const tier = entryTiers.find((t) => t.id === selectedTier);
      return tier?.sc_amount || 0;
    }
    return 0;
  };

  const selectedAmount = getSelectedAmount();
  const previewEntries = calculateEntries(selectedAmount);
  const canAfford = selectedAmount <= scBalance && selectedAmount > 0;

  const handlePurchase = async () => {
    if (!canAfford || !selectedPrize || loading) return;

    setLoading(true);
    setError(null);

    try {
      const result = await purchaseEntries(selectedPrize.id, selectedAmount);
      setEntriesAwarded(result.entries);
      
      // Fetch actual balance after purchase
      try {
        const balanceResponse = await apiClient.get('/redemption/balance');
        if (balanceResponse?.balance) {
          const scBonus = parseFloat(String(balanceResponse.balance.sc_bonus_amount || 0)) || 0;
          const scPromo = parseFloat(String(balanceResponse.balance.sc_promotional_amount || 0)) || 0;
          const scRedeemable = parseFloat(String(balanceResponse.balance.sc_redeemable_amount || 0)) || 0;
          setRemainingBalance(Math.round((scBonus + scPromo + scRedeemable) * 100) / 100);
        }
      } catch {
        // Fallback to calculated balance if fetch fails
        setRemainingBalance(scBalance - selectedAmount);
      }
      
      setSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to purchase entries');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="p-5 sm:p-6 text-center">
        <div className="relative inline-block mb-4">
          <div className="absolute inset-0 bg-emerald-500/30 blur-2xl rounded-full"></div>
          <div className="relative w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-emerald-400 to-green-600 rounded-full flex items-center justify-center shadow-lg shadow-emerald-500/30">
            <Check className="h-8 w-8 sm:h-10 sm:w-10 text-white" strokeWidth={3} />
          </div>
        </div>
        <h3 className="text-xl sm:text-2xl font-black text-white mb-3">Entries Added!</h3>
        <div className="bg-gradient-to-br from-emerald-500/20 to-green-500/10 border border-emerald-500/30 rounded-2xl p-4 sm:p-5 mb-4 backdrop-blur-sm">
          <div className="text-4xl sm:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-green-400">
            +{entriesAwarded.toLocaleString()}
          </div>
          <div className="text-sm text-emerald-400/80 mt-1">entries for {selectedPrize?.name}</div>
        </div>
        <p className="text-gray-400 text-sm mb-5">
          Remaining balance: <span className="text-white font-bold">{formatCurrency(remainingBalance)} SC</span>
        </p>
        <button
          onClick={onSkip}
          className="w-full min-h-[52px] bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-400 hover:to-amber-400 text-black font-bold py-4 rounded-xl transition-all active:scale-[0.98] shadow-lg shadow-yellow-500/20"
        >
          Continue to Redemption
        </button>
      </div>
    );
  }

  if (!selectedPrize) {
    return (
      <div className="p-5 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2.5">
            <div className="bg-gradient-to-br from-yellow-400 to-amber-500 p-2 rounded-lg">
              <Trophy className="h-5 w-5 text-black" />
            </div>
            <h3 className="text-base sm:text-lg font-bold text-white">Win Amazing Prizes!</h3>
          </div>
          <button 
            onClick={onSkip} 
            className="p-2 rounded-lg hover:bg-gray-800 text-gray-400 hover:text-white transition-all active:scale-95"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <p className="text-gray-400 text-sm mb-4">
          Use some SC for a chance to win before you redeem!
        </p>

        {/* Prize Selection List */}
        <div className="space-y-2.5 mb-5">
          {activePrizes.slice(0, 3).map((prize, index) => (
            <button
              key={prize.id}
              onClick={() => setSelectedPrize(prize)}
              className="w-full bg-gray-800/50 border border-gray-700/80 hover:border-yellow-500/40 rounded-xl p-3 flex items-center gap-3 transition-all active:scale-[0.99] group"
            >
              <div className="relative w-14 h-14 bg-gray-700/50 rounded-xl overflow-hidden flex-shrink-0">
                {prize.images?.[0] ? (
                  <img src={prize.images[0]} alt={prize.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Gift className="h-6 w-6 text-gray-600" />
                  </div>
                )}
                {index === 0 && (
                  <div className="absolute top-1 left-1 bg-yellow-500 rounded p-0.5">
                    <Star className="h-2.5 w-2.5 text-black fill-black" />
                  </div>
                )}
              </div>
              <div className="flex-1 text-left min-w-0">
                <div className="font-semibold text-white text-sm truncate">{prize.name}</div>
                <div className="text-yellow-400 font-bold text-sm">${prize.estimated_value.toLocaleString()}</div>
              </div>
              {prize.my_entries > 0 && (
                <div className="bg-emerald-500/20 text-emerald-400 px-2 py-1 rounded-lg text-xs font-semibold flex items-center gap-1">
                  <Ticket className="h-3 w-3" />
                  {prize.my_entries}
                </div>
              )}
              <ChevronRight className="h-5 w-5 text-gray-500 group-hover:text-yellow-400 transition-colors flex-shrink-0" />
            </button>
          ))}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            onClick={onSkip}
            className="flex-1 min-h-[48px] bg-gray-800/80 hover:bg-gray-700 text-gray-300 font-medium py-3 rounded-xl transition-all active:scale-[0.98]"
          >
            Skip
          </button>
          <button
            onClick={onEnter}
            className="flex-1 min-h-[48px] bg-gradient-to-r from-yellow-500/20 to-amber-500/10 border border-yellow-500/40 text-yellow-400 font-bold py-3 rounded-xl hover:bg-yellow-500/20 transition-all active:scale-[0.98]"
          >
            View All
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-5 sm:p-6">
      {/* Header with back button */}
      <div className="flex items-center gap-2 mb-4">
        <button 
          onClick={() => setSelectedPrize(null)} 
          className="p-2 -ml-2 rounded-lg hover:bg-gray-800 text-gray-400 hover:text-white transition-all active:scale-95"
        >
          <ChevronRight className="h-5 w-5 rotate-180" />
        </button>
        <h3 className="text-base sm:text-lg font-bold text-white truncate">Enter for {selectedPrize.name}</h3>
      </div>

      {/* Prize Preview */}
      <div className="flex items-center gap-3 bg-gray-800/50 rounded-xl p-3 mb-4 border border-gray-700/50">
        <div className="w-14 h-14 bg-gray-700/50 rounded-xl overflow-hidden flex-shrink-0">
          {selectedPrize.images?.[0] ? (
            <img src={selectedPrize.images[0]} alt={selectedPrize.name} className="w-full h-full object-cover" />
          ) : (
            <Gift className="w-full h-full p-3 text-gray-600" />
          )}
        </div>
        <div className="min-w-0">
          <div className="text-yellow-400 font-black text-lg">${selectedPrize.estimated_value.toLocaleString()}</div>
          <div className="text-gray-400 text-xs flex items-center gap-1">
            <Ticket className="h-3 w-3" />
            Your entries: {selectedPrize.my_entries.toLocaleString()}
          </div>
        </div>
      </div>

      {/* Tier Selection */}
      <div className="text-xs font-semibold text-gray-400 mb-2 uppercase tracking-wide">Select amount</div>
      <div className="grid grid-cols-2 gap-2 mb-4">
        {[...entryTiers].sort((a, b) => a.sc_amount - b.sc_amount).map((tier, index) => {
          const affordable = tier.sc_amount <= scBalance;
          const isSelected = !useCustom && selectedTier === tier.id;
          // Mark the second tier (usually 1 SC) as popular
          const sortedTiers = [...entryTiers].sort((a, b) => a.sc_amount - b.sc_amount);
          const isPopular = sortedTiers.length > 1 && tier.id === sortedTiers[1]?.id;
          return (
            <button
              key={tier.id}
              onClick={() => {
                setSelectedTier(tier.id);
                setUseCustom(false);
              }}
              disabled={!affordable}
              className={`relative min-h-[72px] p-3 rounded-xl border-2 transition-all active:scale-[0.98] text-left ${
                isSelected
                  ? 'border-yellow-500 bg-gradient-to-br from-yellow-500/15 to-amber-500/10 shadow-lg shadow-yellow-500/10'
                  : affordable
                  ? 'border-gray-700/80 hover:border-gray-600 bg-gray-800/40'
                  : 'border-gray-800/50 bg-gray-800/20 opacity-50'
              }`}
            >
              {isPopular && affordable && (
                <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-gradient-to-r from-yellow-500 to-amber-500 text-black text-[8px] font-bold uppercase px-1.5 py-0.5 rounded-full">
                  Popular
                </div>
              )}
              <div className="font-black text-white text-sm">{formatCurrency(tier.sc_amount)} SC</div>
              <div className="text-yellow-400 text-xs font-semibold">{tier.entries_awarded.toLocaleString()} entries</div>
            </button>
          );
        })}
      </div>

      {/* Preview Box */}
      {selectedAmount > 0 && (
        <div className="bg-gradient-to-br from-yellow-500/10 to-amber-500/5 border border-yellow-500/30 rounded-xl p-4 mb-4 text-center">
          <div className="text-xs text-yellow-400/70 font-medium mb-1">You'll get</div>
          <div className="text-2xl sm:text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-amber-400">
            +{previewEntries.toLocaleString()}
          </div>
          <div className="text-xs text-gray-500">entries</div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 mb-4">
          <p className="text-red-400 text-sm font-medium">{error}</p>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-3">
        <button
          onClick={onSkip}
          className="flex-1 min-h-[48px] bg-gray-800/80 hover:bg-gray-700 text-gray-300 font-medium py-3 rounded-xl transition-all active:scale-[0.98]"
        >
          Skip
        </button>
        <button
          onClick={handlePurchase}
          disabled={!canAfford || loading}
          className={`flex-1 min-h-[48px] py-3 rounded-xl font-bold transition-all active:scale-[0.98] flex items-center justify-center gap-2 ${
            canAfford
              ? 'bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-400 hover:to-amber-400 text-black shadow-lg shadow-yellow-500/20'
              : 'bg-gray-700 text-gray-500 cursor-not-allowed'
          }`}
        >
          {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Enter Prize'}
        </button>
      </div>
    </div>
  );
}
